<script setup lang="ts">
import { scrollToTop } from '@/shared/utils/scrollBehavior';
import type { RouteLocationRaw } from 'vue-router';
import type {
  PaginationItem,
  Props,
} from '@/shared/ui/AppPagination/model/types/appPagination';

defineOptions({
  name: 'AppPagination',
});

const props = withDefaults(defineProps<Props>(), {
  modelValue: 1,
  totalItems: 0,
  perPage: 10,
  isScrollToTop: true,
  isLink: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue', page: number): void;
}>();

const route = useRoute();
const isMobile: boolean = useIsMobile();

const totalPages: ComputedRef<number> = computed(() =>
  Math.max(1, Math.ceil(props.totalItems / props.perPage)),
);
const isFirst: ComputedRef<boolean> = computed(() => props.modelValue <= 1);
const isLast: ComputedRef<boolean> = computed(() => props.modelValue >= totalPages.value);

const items: ComputedRef<PaginationItem[]> = computed(() => {
  const total = totalPages.value;
  const current = props.modelValue;
  const maxFullRow = isMobile ? 5 : 7;

  if (total <= maxFullRow) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  if (current <= 4) {
    return isMobile ? [1, 2, 3, 4, '...', total] : [1, 2, 3, 4, 5, '...', total];
  }

  if (current >= total - 3) {
    return isMobile
      ? [1, '...', total - 3, total - 2, total - 1, total]
      : [1, '...', total - 4, total - 3, total - 2, total - 1, total];
  }

  return isMobile
    ? [1, '...', current - 1, current, '...', total]
    : [1, '...', current - 1, current, current + 1, '...', total];
});

const pageLink = (page: number): RouteLocationRaw => ({
  path: route.path,
  query: { ...route.query, page: page === 1 ? undefined : String(page) },
});

const selectPage = (page: number): void => {
  if (page === props.modelValue) return;
  emit('update:modelValue', page);
  if (props.isScrollToTop) scrollToTop();
};

const goPrevious = (): void => {
  if (isFirst.value) return;
  selectPage(props.modelValue - 1);
};

const goNext = (): void => {
  if (isLast.value) return;
  selectPage(props.modelValue + 1);
};
</script>

<template>
  <ul :class="['app-pagination', { _mobile: isMobile }]" data-testid="pagination">
    <li class="app-pagination__item">
      <NuxtLink
        v-if="isLink && !isFirst"
        class="app-pagination__button"
        :to="pageLink(modelValue - 1)"
        aria-label="Предыдущая страница"
      >
        <AppIcon name="ArrowLeftPagination" />
      </NuxtLink>
      <button
        v-else
        :class="['app-pagination__button', { _disabled: isFirst }]"
        type="button"
        aria-label="Предыдущая страница"
        :disabled="isFirst"
        @click="goPrevious"
      >
        <AppIcon name="ArrowLeftPagination" />
      </button>
    </li>
    <li v-for="(item, i) in items" :key="`${item}-${i}`" class="app-pagination__item">
      <span v-if="item === '...'" class="app-pagination__item-more">{{ item }}</span>
      <NuxtLink
        v-else-if="isLink"
        :class="['app-pagination__item-button', { _active: item === modelValue }]"
        :to="pageLink(item)"
        :aria-label="`Страница ${item}`"
        :aria-current="item === modelValue ? 'page' : undefined"
      >
        {{ item }}
      </NuxtLink>
      <button
        v-else
        :class="['app-pagination__item-button', { _active: item === modelValue }]"
        type="button"
        :aria-label="`Страница ${item}`"
        :aria-current="item === modelValue ? 'page' : undefined"
        @click="selectPage(item)"
      >
        {{ item }}
      </button>
    </li>
    <li class="app-pagination__item">
      <NuxtLink
        v-if="isLink && !isLast"
        class="app-pagination__button"
        :to="pageLink(modelValue + 1)"
        aria-label="Следующая страница"
      >
        <AppIcon name="ArrowRightPagination" />
      </NuxtLink>
      <button
        v-else
        :class="['app-pagination__button', { _disabled: isLast }]"
        type="button"
        aria-label="Следующая страница"
        :disabled="isLast"
        @click="goNext"
      >
        <AppIcon name="ArrowRightPagination" />
      </button>
    </li>
  </ul>
</template>

<style lang="less">
.app-pagination {
  display: inline-flex;
  align-items: center;
  background-color: @color-black-haze;
  padding: @main-size;
  border-radius: 6px;

  &__button {
    background-color: @color-gallery;
    border-radius: @main-size;
    padding: 5px 10px;
    height: 28px;
    transition: background-color 0.1s linear;

    &:hover {
      background-color: @color-silver-chalice;

      svg path {
        stroke: white;
      }
    }

    &:active {
      background-color: @color-mercury;
    }

    &._disabled {
      cursor: not-allowed;
      pointer-events: none;

      svg path {
        stroke: @color-silver-chalice;
      }
    }
  }

  &__item {
    display: flex;
    align-items: center;

    &:not(:last-child) {
      margin-right: @size-x5;
    }
  }

  &__item-more {
    display: inline-flex;
    align-items: flex-end;
    justify-content: center;
  }

  &__item-button {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 13px;
    line-height: 18px;
    font-weight: 500;
    background-color: @color-gallery;
    border-radius: @main-size;
    width: 28px;
    height: 28px;
    transition: background-color 0.1s linear;

    &:hover {
      background-color: @color-silver-chalice;
      color: white;
    }

    &:active {
      background-color: @color-mercury;
    }

    &._active {
      color: #fff;
      background: @color-persimmon;
    }
  }

  &._mobile &__item {
    &:not(:last-child) {
      margin-right: @size-x3;
    }
  }
}
</style>
